import React from 'react'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { WebhookDeliveryActionsCell } from 'src/components/webhook/grid/WebhookDeliveryActionsCell'
import { WebhookDeliveryResultCell } from 'src/components/webhook/grid/WebhookDeliveryResultCell'
import type { WebhookDelivery } from 'src/interfaces/webhook'
import { formatDateTime, parseUnixTimestamp } from 'src/utils/grid'
import { webhookEventLabels, webhookDeliveriesTableHeaderTitles } from './constants'
import { WebhookDeliveriesTableFields } from './enums'

/**
 * Wiring only. `event` is an optional left-joined relation, so every read of it is guarded and
 * renders an em-dash when absent — a cosmetic default, not substituted business content.
 *
 * A factory rather than a module constant: the actions column closes over the page's details modal
 * handler. Callers must memoize the result.
 */
export const buildWebhookDeliveryColumns = (
  onViewDelivery: (delivery: WebhookDelivery) => void,
): Array<GridColDef<WebhookDelivery>> => {
  return [
    {
      field: WebhookDeliveriesTableFields.deliveryStatus,
      headerName: webhookDeliveriesTableHeaderTitles[WebhookDeliveriesTableFields.deliveryStatus],
      width: 140,
      // The list is ordered `delivery.createdAt DESC` by the backend and paged on the server, so
      // no column is client-sortable — a client sort would only reorder the current page.
      sortable: false,
      hideable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<WebhookDelivery>) => {
        return <WebhookDeliveryResultCell deliveryStatus={params.row.deliveryStatus} />
      },
    },
    {
      field: WebhookDeliveriesTableFields.eventType,
      headerName: webhookDeliveriesTableHeaderTitles[WebhookDeliveriesTableFields.eventType],
      flex: 2,
      minWidth: 200,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<WebhookDelivery>) => {
        const eventType = params.row.event?.eventType

        if (!eventType) {
          return (
            <Typography variant="body2" css={tw`text-shades-500`}>
              —
            </Typography>
          )
        }

        return (
          <Typography variant="body2" css={tw`truncate`}>
            {webhookEventLabels[eventType] ?? eventType}
          </Typography>
        )
      },
    },
    {
      field: WebhookDeliveriesTableFields.resourceId,
      headerName: webhookDeliveriesTableHeaderTitles[WebhookDeliveriesTableFields.resourceId],
      flex: 2,
      minWidth: 180,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<WebhookDelivery>) => {
        const resourceId = params.row.event?.resourceId

        if (!resourceId) {
          return (
            <Typography variant="body2" css={tw`text-shades-500`}>
              —
            </Typography>
          )
        }

        return (
          <Typography variant="body2" css={tw`truncate`}>
            {resourceId}
          </Typography>
        )
      },
    },
    {
      field: WebhookDeliveriesTableFields.createdAt,
      headerName: webhookDeliveriesTableHeaderTitles[WebhookDeliveriesTableFields.createdAt],
      width: 190,
      sortable: false,
      type: 'dateTime',
      // A delivery log needs the time of day, not just the date.
      valueGetter: parseUnixTimestamp,
      valueFormatter: formatDateTime,
    },
    {
      field: WebhookDeliveriesTableFields.actions,
      headerName: webhookDeliveriesTableHeaderTitles[WebhookDeliveriesTableFields.actions],
      width: 68,
      sortable: false,
      hideable: false,
      disableExport: true,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<WebhookDelivery>) => {
        return <WebhookDeliveryActionsCell delivery={params.row} onViewDetails={onViewDelivery} />
      },
    },
  ]
}
